/* eslint sort-keys: error */
/* eslint-disable max-lines */
import { v4 as uuidv4 } from 'uuid';
import type { AppletDesignMenuItem } from '@redviking/argonaut-core-ui/src/applet/design/util/AppletDesignMenuItem';
import type { Component } from 'vue';
import { messages } from 'src/i18n/i18n';
import { validateComparisons } from '@redviking/argonaut-core-ui/src/mes/lib/validate-comparison';
import { gatherComparisonInputs } from '@redviking/argonaut-util/src/mes/gather-comparison-inputs';
import type { EntityTypeMap } from 'types/entity';
import cellTypeMap from '../cells/cellTypeMap';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { ValidationResult } from 'types';
import { getAllAppletVariables } from '@redviking/argonaut-util/src/mes/appletUtil';
import { macroInfoMap } from '@redviking/argonaut-util/src/mes/macro-info-map';

type PartialAttrs<T> = T extends object ? { [P in keyof T]?: Partial<T[P]> } : T;

export interface MacroMapItem<M extends Latest.Macros.AppletMacro = Latest.Macros.AppletMacro> extends AppletDesignMenuItem<M['type']> {
    defaultMacro: (cfg?: PartialAttrs<M>) => {
        macro: M;
    };
    validator: (macroCfg: M, entityData: EntityTypeMap['appletDesignVersion']) => ValidationResult;
    tab: () => Promise<Component>;
    icon: string;
    cellTarget: keyof typeof cellTypeMap;
}

export type MacroTypeMap = {
    [macroTypeKey in Latest.Macros.AppletMacro['type']]: MacroMapItem<Latest.Macros.AppletMacro & { type: macroTypeKey }>;
};

export const macroTypeMap: MacroTypeMap = {
    materialLookup: {
        cellTarget: 'materialLookup',
        defaultMacro: cfg => {
            const macroId = uuidv4();
            const materialIdVarId = getUID();
            const serialNumberVarId = getUID();

            const macro: Latest.Macros.MaterialLookup = {
                ...cfg,
                attrs: {
                    ...cfg?.attrs,
                    materialLookupAttrs: cfg?.attrs?.materialLookupAttrs || {
                        filterConditions: [
                            {
                                boolOperation: 'and',
                                comparisons: [],
                                evalTrigger: 'varChange',
                            },
                        ],
                        matClassId: '',
                        matModelIds: [],
                        outputVars: {
                            materialIdVar: {
                                varCfg: {
                                    defaultValue: '',
                                    generatedByMacroId: macroId,
                                    idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                    kind: 'generated',
                                    name: 'materialId',
                                    sparkplug: {
                                        tracked: false,
                                        writable: false,
                                    },
                                    type: 'str',
                                 },
                               varId: materialIdVarId,
                            },
                            serialNumberVar: {
                                varCfg: {
                                    defaultValue: '',
                                    generatedByMacroId: macroId,
                                    idx: 999, // setting high idx to ensure generated variables are at the bottom of the list
                                    kind: 'generated',
                                    name: 'serialNumber',
                                    sparkplug: {
                                        tracked: false,
                                        writable: false,
                                    },
                                    type: 'str',
                                },
                                varId: serialNumberVarId,
                            },
                            udfs: [],
                        },
                        refreshCondition: {
                            boolOperation: 'and',
                            comparisons: [],
                            evalTrigger: 'varChange',
                        },
                        udfColsToShow: [],
                    },

                },
                id: macroId,
                name: '',
                type: 'materialLookup',
            };

            return {
                macro,
            };
        },
        description: messages.mes.cells.materialLookup.description,
        icon: macroInfoMap.materialLookup.icon,
        tab: () => import('src/applet/design/macros/material-lookup/MaterialLookup.tab.macro.vue').then(m => m.default),
        text: messages.mes.cells.materialLookup.title,
        type: 'materialLookup',
        validator: (macro, entityData) => {
            const materialLookupAttrs = macro.attrs.materialLookupAttrs;

            if (!materialLookupAttrs.outputVars.materialIdVar.varId || !materialLookupAttrs.outputVars.materialIdVar.varCfg.name) {
                return {
                    errorMessage: 'Macro output variable config for material id is invalid.',
                    status: 'error',
                };
            }

            if (!materialLookupAttrs.outputVars.serialNumberVar.varId || !materialLookupAttrs.outputVars.serialNumberVar.varCfg.name) {
                return {
                    errorMessage: 'Macro output variable config for serial number is invalid.',
                    status: 'error',
                };
            }
            
            for (const udf of materialLookupAttrs.outputVars.udfs) {
                if (!udf.varCfg.varId || !udf.varCfg.varCfg.name) {
                    return {
                        errorMessage: 'Macro output variable config for udf is invalid.',
                        status: 'error',
                    };
                }
            }

            // Do not need to validate macro conditions since the only thing the validate function does it check for var names in conditions that compare vars
            // Material lookup conditions are not allowed to compare vars
            if (macro.attrs.materialLookupAttrs.refreshCondition.evalTrigger === 'varChange') {
                const inputs = gatherComparisonInputs(macro.attrs.materialLookupAttrs.refreshCondition.comparisons);
                const allAppletVariables = getAllAppletVariables(entityData.config);
                for (const appletVarId of inputs) {
                    if (!allAppletVariables[appletVarId]) {
                        return {
                            errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({
                                cellLabel: macro.name,
                                cellType: macro.type,
                                varId: appletVarId,
                            }),
                            status: 'error',
                        };
                    }
                }
                const validateComparisonsResult = validateComparisons(macro.attrs.materialLookupAttrs.refreshCondition.comparisons);
                if (validateComparisonsResult.status !== 'success') {
                    return validateComparisonsResult;
                }
            } else if (macro.attrs.materialLookupAttrs.refreshCondition.evalTrigger === 'event') {
                if (!macro.attrs.materialLookupAttrs.refreshCondition.eventTrigger) {
                    return {
                        errorMessage: 'Input cell event trigger needs an even',
                        status: 'error',
                    };
                }
            }
            return {
                status: 'success',
            };
        },
    },
};

export const macroTypeMapItems = Object.keys(macroTypeMap).map(k => macroTypeMap[k as Latest.Macros.AppletMacroType]);

export default macroTypeMap;
